import { API } from '~apis/axios/instance';
import { CashFlowType } from '~models/cashFlow';
import { ResponseBaseWithData } from '~types/response';

interface CompanySpecificCategoryCreateRequest {
  companyId: string;
  categoryId: number;
  request: {
    name: string;
    description?: string;
    parentId?: number;
  };
}

interface CategoryTreeResponse {
  tree: ITree;
}
interface ITree {
  children: CategoryTree[];
}
export type CategoryTree = {
  key: string;
  value: CategoryTreeItem;
  children: CategoryTree[];
};

export type CategoryTreeItem = {
  id: number;
  name: string;
  description?: string | null;
  cashFlowType: CashFlowType;
  sequenceNumber: number;
  isUnClassified: boolean;
  needMapping: boolean;
  hasBusinessEntity: boolean;
  contraCategoryId: number;
  isCardPayment: boolean;
  isActive: boolean;
  type: CategorySystemType;
  categoryLabelKey: string | null;
  contraCategoryLabelKey: string | null;
};

export type CategorySystemType = 'CATEGORY1' | 'CATEGORY2' | 'COMPANY';

export const getCategoryTree = ({ companyId }: { companyId: string }) => {
  return API.get<ResponseBaseWithData<CategoryTreeResponse>>(
    `/api/v3/companies/${companyId}/categories/tree`,
  );
};

export const addCompanySpecificCategories = ({
  companyId,
  categoryId,
  request,
}: CompanySpecificCategoryCreateRequest) => {
  return API.post<
    ResponseBaseWithData<{
      categoryTreeKey: string;
    }>
  >(`/api/v3/companies/${companyId}/categories/${categoryId}/company-detailed-categories`, {
    data: request,
  });
};

export const editCompanySpecificCategories = ({
  companyId,
  categoryId,
  id,
  request,
}: CompanySpecificCategoryCreateRequest & {
  id: number;
}) => {
  return API.put<ResponseBaseWithData>(
    `/api/v3/companies/${companyId}/categories/${categoryId}/company-detailed-categories/${id}`,
    {
      data: request,
    },
  );
};

export const deleteCompanySpecificCategories = ({
  companyId,
  categoryId,
  id,
  deleteRequest,
}: Omit<CompanySpecificCategoryCreateRequest, 'request'> & {
  id: number;
  deleteRequest?: {
    moveChildrenTo?: string;
    migrations?: {
      from: {
        categoryId: number;
        companyDetailedCategoryId?: number;
      };
      to: {
        categoryId: number;
        companyDetailedCategoryId?: number;
      };
    }[];
  };
}) => {
  return API.delete<ResponseBaseWithData>(
    `/api/v3/companies/${companyId}/categories/${categoryId}/company-detailed-categories/${id}`,
    {
      data: deleteRequest,
    },
  );
};

export const reorderCompanySpecificCategories = ({
  companyId,
  categoryId,
  request,
}: {
  companyId: string;
  categoryId: number;
  request: {
    parentCategoryTreeKey: string;
    ids: number[];
  };
}) => {
  return API.patch<ResponseBaseWithData>(
    `/api/v3/companies/${companyId}/categories/${categoryId}/company-detailed-categories/reorder`,
    {
      data: request,
    },
  );
};

export const toggleActiveCompanySpecificCategories = ({
  companyId,
  categoryId,
  id,
  active,
}: {
  companyId: string;
  categoryId: number;
  id: number;
  active: boolean;
}) => {
  return API.patch<ResponseBaseWithData>(
    `/api/v3/companies/${companyId}/categories/${categoryId}/company-detailed-categories/${id}/active`,
    {
      data: {
        active,
      },
    },
  );
};

export const moveParentCategory = ({
  companyId,
  categoryId,
  id,
  newParentKey,
}: {
  companyId: string;
  categoryId: number;
  id: number;
  newParentKey: {
    id: number;
    type: string;
  };
}) => {
  return API.patch(
    `/api/v3/companies/${companyId}/categories/${categoryId}/company-detailed-categories/${id}/move`,
    {
      data: {
        newParentKey,
      },
    },
  );
};

export const getCategoryUsedCounts = ({
  companyId,
  categoryId,
  id,
  includeChildren = false,
}: {
  companyId: string;
  categoryId: number;
  id: number;
  includeChildren?: boolean;
}) => {
  return API.get<ResponseBaseWithData<{ items: { key: string; count: number }[] }>>(
    `/api/v3/companies/${companyId}/categories/${categoryId}/company-detailed-categories/${id}/counts?includeChildren=${includeChildren}`,
  );
};
